'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation'; // Navigation hook
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// --- SLIDER DATA ---
const slides = [
  {
    id: 1,
    image: '/ArtofAtmosphere1.jpg',
    title: 'THE ART OF ATMOSPHERE',
    subtitle: 'WHERE LUXURY MEETS TRANQUILITY',
    cta: 'VISIT US',
    link: '/join',
    align: 'center'
  },
  {
    id: 2,
    image: '/aura-treadmill-window.jpg',
    title: 'CARDIO ELITE',
    subtitle: 'ELEVATE YOUR ENERGY WITH A VIEW',
    cta: 'START TRAINING',
    link: '/join',
    align: 'left'
  },
  {
    id: 3,
    image: '/aura-premium-facilities.jpg',
    title: 'DEFINE YOUR BALANCE',
    subtitle: 'STATE-OF-THE-ART PILATES STUDIO',
    cta: 'DISCOVER PILATES',
    link: '/join',
    align: 'right'
  }
];

export default function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false); // Loading state
  const router = useRouter(); // Router init

  // Auto-play logic
  useEffect(() => {
    const timer = setInterval(() => {
      // Agar navigation ho rahi hai to auto-slide rok dein
      if (!isNavigating) {
        nextSlide();
      }
    }, 6000);
    return () => clearInterval(timer);
  }, [current, isNavigating]);

  const nextSlide = () => {
    setCurrent((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrent((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  // Handle Button Click
  const handleNavigate = (link) => {
    setIsNavigating(true); // Spinner start
    router.push(link);     // Go to page
  };

  // Animation Variants
  const textVariant = {
    hidden: { y: 50, opacity: 0 },
    visible: (delay) => ({
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, delay: delay, ease: "easeOut" }
    })
  };

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black">
      
      <AnimatePresence mode="wait">
        {slides.map((slide, index) => (
          index === current && (
            <motion.div
              key={slide.id}
              className="absolute inset-0 w-full h-full"
              initial={{ scale: 1.2, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }}    
              exit={{ opacity: 0 }}
              transition={{ duration: 2, ease: "easeOut" }}
            >
              {/* IMAGE */}
              <Image
                src={slide.image}
                alt={slide.title}
                fill
                priority={index === 0}
                className="object-cover"
                sizes="100vw"
                quality={85}
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-black/40" />

              {/* TEXT CONTENT */}
              <div className={`absolute inset-0 flex items-center px-6 sm:px-12 ${
                slide.align === 'left' ? 'justify-start' : 
                slide.align === 'right' ? 'justify-end' : 'justify-center'
              }`}>
                <div className={`max-w-4xl text-white ${
                  slide.align === 'left' ? 'text-left' : 
                  slide.align === 'right' ? 'text-right' : 'text-center'
                }`}>
                  
                  {/* Subtitle */}
                  <motion.p 
                    custom={0.5}
                    variants={textVariant}
                    initial="hidden"
                    animate="visible"
                    className="text-[#faaf40] font-bold tracking-[0.2em] mb-4 uppercase text-sm sm:text-lg"
                  >
                    {slide.subtitle}
                  </motion.p>

                  {/* Title */}
                  <motion.h1 
                    custom={0.7}
                    variants={textVariant}
                    initial="hidden"
                    animate="visible"
                    className="text-4xl sm:text-6xl md:text-8xl font-extrabold uppercase leading-tight mb-8 drop-shadow-lg"
                  >
                    {slide.title}
                  </motion.h1>

                  {/* Button with Loading Spinner */}
                  <motion.div
                    custom={0.9}
                    variants={textVariant}
                    initial="hidden"
                    animate="visible"
                  >
                    <button 
                      onClick={() => handleNavigate(slide.link)}
                      disabled={isNavigating}
                      className="inline-flex items-center justify-center gap-3 bg-[#faaf40] text-black font-bold uppercase py-4 px-10 rounded hover:bg-white hover:text-black transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-80 disabled:cursor-not-allowed min-w-[200px]"
                    >
                      {isNavigating ? (
                        <>
                          <svg className="animate-spin h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Processing...</span>
                        </>
                      ) : (
                        slide.cta
                      )}
                    </button>
                  </motion.div>

                </div>
              </div>
            </motion.div>
          )
        ))}
      </AnimatePresence>

      {/* --- CONTROLS --- */}
      <button 
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-[#faaf40] transition-colors p-3 z-20 hover:bg-black/20 rounded-full"
      >
        <ChevronLeft size={48} />
      </button>

      <button 
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-[#faaf40] transition-colors p-3 z-20 hover:bg-black/20 rounded-full"
      >
        <ChevronRight size={48} />
      </button>

      {/* Dots */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-3 z-20">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrent(idx)}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              current === idx ? 'w-10 bg-[#faaf40]' : 'w-3 bg-white/50 hover:bg-white'
            }`}
          />
        ))}
      </div>

    </div>
  );
}